import * as React from "react";
import { Modal, Tabs } from "antd";
import { v4 as uuidv4 } from "uuid";
import { PlusOutlined } from "@ant-design/icons";
import { getDefaultUnmanagedNodePoolInput } from "../../../draft/utils/utils";
import { LambdalabsNoUnmanagedNodePool } from "./noUnmanagedNodePool";
import { LambdalabsUnmanagedNodePool } from "./unmanagedNodePool";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { cloneDeep } from "lodash";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { TabLabel } from "../../../../workload/draft/tabLabel";
import { Mk8sCluster } from "../../../../../schema/types/mk8s/entity/mk8s";

type TargetKey = React.MouseEvent | React.KeyboardEvent | string;

interface Props {
  mk8s?: Mk8sCluster;
}
export const LambdalabsUnmanagedNodePools: React.FC<Props> = ({ mk8s }) => {
  const { id } = useCpFormContext();
  const unmanagedNodePools = useCpFormValue("lambdalabs.unmanagedNodePools");
  const activeKey = useCpFormValue(`meta.lambdalabs.activeKeyUnmanagedNodePool`) || unmanagedNodePools[0]?.id || "";

  function onEdit(targetKey: React.MouseEvent | React.KeyboardEvent | string, action: "add" | "remove") {
    if (action === "add") {
      add();
    } else {
      Modal.confirm({
        title: "Remove Unmanaged Node Pool",
        content: `Configuration that was made on this node pool will be lost`,
        onOk() {
          remove(targetKey);
        },
      });
    }
  }

  function add() {
    const newActiveKey = uuidv4();

    const _nodePools = cloneDeep(unmanagedNodePools);
    _nodePools.push({
      ...getDefaultUnmanagedNodePoolInput(),
      id: newActiveKey,
    });
    formSetValue(id, `lambdalabs.unmanagedNodePools`, _nodePools);

    formSetValue(id, `meta.lambdalabs.activeKeyUnmanagedNodePool`, newActiveKey);
  }

  function remove(targetKey: TargetKey) {
    let newActiveKey = activeKey;
    let lastIndex = -1;
    let removeIndex = -1;

    unmanagedNodePools.forEach((item, i) => {
      if (item.id === targetKey) {
        lastIndex = i - 1;
        removeIndex = i;
      }
    });

    if (removeIndex >= 0) {
      const _unmanagedNodePools = cloneDeep(unmanagedNodePools);
      _unmanagedNodePools.splice(removeIndex, 1);
      formSetValue(id, `lambdalabs.unmanagedNodePools`, _unmanagedNodePools);
      formTriggerValidation(id);

      if (unmanagedNodePools.length > 1 && newActiveKey === targetKey) {
        if (lastIndex >= 0) {
          newActiveKey = unmanagedNodePools[lastIndex].id!;
        } else {
          newActiveKey = unmanagedNodePools[removeIndex + 1]?.id || unmanagedNodePools[0]?.id || "";
        }
        formSetValue(id, `meta.lambdalabs.activeKeyUnmanagedNodePool`, newActiveKey);
      }
    }
  }

  function onChange(newActiveKey: string) {
    formSetValue(id, `meta.lambdalabs.activeKeyUnmanagedNodePool`, newActiveKey);
  }

  return (
    <div className="cpln-form-section cpln-ant-card-container cpln-ant-tabs-container">
      <Tabs
        type={"editable-card"}
        items={unmanagedNodePools.map((np, i) => {
          const label = np.name || "Unmanaged Node Pool";

          return {
            key: np.id!,
            label: <TabLabel label={label} names={[`lambdalabs.unmanagedNodePools.${i}`]} showDirty />,
            closable: true,
            children: <LambdalabsUnmanagedNodePool index={i} />,
          };
        })}
        onChange={onChange}
        activeKey={activeKey}
        onEdit={onEdit}
        addIcon={
          <div className="flex items-center gap-2 px-4">
            <div>Add</div>
            <PlusOutlined />
          </div>
        }
      />
      {unmanagedNodePools.length < 1 ? <LambdalabsNoUnmanagedNodePool /> : null}
    </div>
  );
};
